import { createTheme, Theme } from '@mui/material/styles';
import { getTheme } from '@allenai/varnish2';

/**
 * THOR theme color constants
 * All hex colors used throughout the theme are defined here for consistency
 */
export const THOR_COLORS = {
  darkGreen: '#032629',
  darkGreen09: '#0a3235e6',

  cream: '#FAF2E9', // Light cream
  lightGreen: '#0FCB8C', // Green/teal - for external links
  pink: '#F0529C', // Pink from logo - for internal links
} as const;

/**
 * THOR-themed custom theme extending Varnish theme
 * Features:
 * - Dark, powerful background colors (stormy blues and deep grays)
 * - Light cream text color (#FAF2E9)
 * - Manrope font family
 */
export function createThorTheme(routerTheme?: unknown): Theme {
  // Get the base Varnish theme
  // routerTheme should be the result of getRouterOverriddenTheme() if provided
  const baseTheme = routerTheme ? getTheme(routerTheme) : getTheme();

  // Create custom theme extending the base theme
  const thorTheme = createTheme({
    ...baseTheme,
    typography: {
      ...baseTheme.typography,
      fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      // Set default text color
      body1: {
        ...baseTheme.typography.body1,
        color: THOR_COLORS.cream,
      },
      body2: {
        ...baseTheme.typography.body2,
        color: THOR_COLORS.cream,
      },
      h1: {
        ...baseTheme.typography.h1,
        color: THOR_COLORS.cream,
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h2: {
        ...baseTheme.typography.h2,
        color: THOR_COLORS.cream,
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h3: {
        ...baseTheme.typography.h3,
        color: THOR_COLORS.cream,
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h4: {
        ...baseTheme.typography.h4,
        color: THOR_COLORS.cream,
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h5: {
        ...baseTheme.typography.h5,
        color: THOR_COLORS.cream,
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h6: {
        ...baseTheme.typography.h6,
        color: THOR_COLORS.cream,
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      },
    },
    palette: {
      ...baseTheme.palette,
      mode: 'dark',
      background: {
        ...baseTheme.palette.background,
        default: THOR_COLORS.darkGreen,
        paper: THOR_COLORS.darkGreen,
      },
      text: {
        ...baseTheme.palette.text,
        primary: THOR_COLORS.cream,
      },
    },
    components: {
      ...baseTheme.components,
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: THOR_COLORS.darkGreen,
            color: THOR_COLORS.cream,
            fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            // Default to internal link color (pink)
            color: THOR_COLORS.lightGreen,
            '&:hover': {
              color: THOR_COLORS.lightGreen,
              opacity: 0.8,
            },
            '&:visited': {
              color: THOR_COLORS.lightGreen,
            },
            // External links (http/https or target="_blank")
            '&[href^="http"]': {
              color: THOR_COLORS.pink,
              '&:hover': {
                color: THOR_COLORS.pink,
                opacity: 0.8,
              },
              '&:visited': {
                color: THOR_COLORS.pink,
              },
            },
            '&[target="_blank"]': {
              color: THOR_COLORS.pink,
              '&:hover': {
                color: THOR_COLORS.pink,
                opacity: 0.8,
              },
              '&:visited': {
                color: THOR_COLORS.pink,
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            // Internal links (default to pink)
            '&[href]:not([href^="http"]):not([target="_blank"])': {
              color: THOR_COLORS.lightGreen,
              '&:hover': {
                color: THOR_COLORS.lightGreen,
                opacity: 0.8,
              },
            },
            // External links
            '&[href^="http"]': {
              color: THOR_COLORS.pink,
              '&:hover': {
                color: THOR_COLORS.pink,
                opacity: 0.8,
              },
            },
            '&[target="_blank"]': {
              color: THOR_COLORS.pink,
              '&:hover': {
                color: THOR_COLORS.pink,
                opacity: 0.8,
              },
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            backgroundColor: '#0c3235',
            textAlign: 'left',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: `${THOR_COLORS.cream} !important`,
            '&::before': {
              display: 'none !important',
            },
            '&::after': {
              display: 'none !important',
            },
            '& .MuiChip-label': {
              color: `${THOR_COLORS.cream} !important`,
            },
          },
          outlined: {
            backgroundColor: 'rgba(43, 68, 70)',
            backgroundImage: 'none !important',
            color: THOR_COLORS.cream,
            borderColor: 'rgba(43, 68, 70) !important',
            '&::before': {
              display: 'none !important',
            },
            '&::after': {
              display: 'none !important',
            },
            '& .MuiChip-label': {
              color: `${THOR_COLORS.cream} !important`,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            // Unselected state: dark background, cream text
            backgroundColor: THOR_COLORS.darkGreen,
            color: THOR_COLORS.cream,
            borderColor: `${THOR_COLORS.cream} !important`,
            border: 'none !important',
            '&:hover': {
              backgroundColor: THOR_COLORS.darkGreen,
              opacity: 0.8,
            },
            // Selected state: light green background, dark text
            '&.Mui-selected': {
              backgroundColor: `${THOR_COLORS.lightGreen} !important`,
              color: `${THOR_COLORS.darkGreen} !important`,
              borderColor: `${THOR_COLORS.lightGreen} !important`,
              '&:hover': {
                backgroundColor: `${THOR_COLORS.lightGreen} !important`,
                opacity: 0.9,
              },
              // Ensure icons and text inside are dark when selected
              '& svg': {
                color: `${THOR_COLORS.darkGreen} !important`,
              },
            },
            // Ensure icons are cream when unselected
            '&:not(.Mui-selected) svg': {
              color: `${THOR_COLORS.cream} !important`,
              border: 'none !important',
            },
          },
        },
      },
    },
  });

  return thorTheme;
}
